import { useMemo } from 'react'

import { useGateValue } from '~/configs/statsig'
import { useCurrentOrgId, useIsCloaking } from '~/store/slices/currentOrg'

import type { DepotModel } from '../../client/chargers'
import { useGetDepotsQuery } from '../../client/chargers'
import { useGetSharingAgreementsQuery } from '../../client/fleet-management'
import { type PollingOptions, usePolling } from '../polling'
import useManagedOrgTree from './organizations'
import useDataManager from './useDataManager'
import { useNormalizedFilters } from './utils'

type SiteFilters = {
  orgId?: string
}

type UseManagedSitesInput = {
  filters?: SiteFilters
  options?: PollingOptions
}

const ORG_SCOPED_DEPOTS_PAGE_SIZE = 1000

/** Item statuses that never grant visibility, even on an ACTIVE agreement. */
const INACTIVE_ITEM_STATUSES = new Set(['PENDING', 'REVOKED', 'REJECTED'])

export default function useManagedSites({ filters, options }: UseManagedSitesInput = {}) {
  const { sites } = useDataManager()
  const { data, isLoading, isFetching, ...rest } = sites
  const { isLoading: isOrgTreeLoading, isFetching: isOrgTreeFetching } = useManagedOrgTree()
  const { sites: filteredSites, isSharesLoading, isSharesFetching } = useFilteredSites(data, filters)

  usePolling(sites, options)

  return {
    ...rest,
    sites: filteredSites,
    isLoading: isLoading || isOrgTreeLoading || isSharesLoading,
    isFetching: isFetching || isOrgTreeFetching || isSharesFetching
  }
}

type ReceivedShares = {
  /** Org IDs shared to the org; matched against `site.fleetId`. */
  orgIds: Set<string>
  /** Site IDs shared to the org; matched against `site.depotId`. */
  siteIds: Set<string>
  isLoading: boolean
  isFetching: boolean
}

/**
 * Whether Synop-internal cloaking should resolve shares for `orgId`.
 *
 * Shares attach to the cloaked org's root only (parity with a real admin of that
 * org — see `OrgTree`), so child-org filters skip the extra requests entirely.
 */
function useShouldResolveCloakedShares(orgId?: string): boolean {
  const currentOrgId = useCurrentOrgId()
  const isCloaking = useIsCloaking()
  const isSynopInternal = useGateValue('synop_internal_only')
  return Boolean(isSynopInternal && isCloaking && orgId && orgId === currentOrgId)
}

/**
 * Assets shared *to* the cloaked org, for Synop staff only.
 *
 * Cloaking swaps `X-Organization-Id` but not the JWT, so the manager caches stay
 * principal-scoped: Synop's site list may already contain the shared sites, but
 * nothing in it marks them as shared to this org — `DepotModel` carries only the
 * owner's `fleetId`, and the `SHARED` sentinel exists solely in `getCustomers`
 * responses for the org that received the share. The sharing agreements endpoint
 * is the only principal-side source that names the recipient.
 */
function useReceivedShares(orgId: string | undefined, enabled: boolean): ReceivedShares {
  const skip = !enabled || !orgId

  // `currentData` (not `data`) is arg-scoped, so a stale org's agreements are never
  // served at the same reference after the cloaked org changes.
  const { currentData, isLoading, isFetching } = useGetSharingAgreementsQuery({ id: orgId ?? '' }, { skip })

  return useMemo(() => {
    const orgIds = new Set<string>()
    const siteIds = new Set<string>()

    // Never widen the filter off a skipped query's payload — the requested org is not
    // the cloaked one, so its shares must not leak into the result.
    for (const agreement of skip ? [] : (currentData ?? [])) {
      if (agreement.role !== 'GRANTEE' || agreement.status !== 'ACTIVE') continue

      for (const item of agreement.items ?? []) {
        if (!item.assetId) continue
        // ACTIVE agreements sometimes omit per-item status; only exclude items the
        // backend has explicitly marked as not yet / no longer in effect.
        if (item.status && INACTIVE_ITEM_STATUSES.has(item.status)) continue
        // FLEET and ASSET shares cover vehicles and individual assets, neither of which
        // grants site visibility. Org shares are taken as the literal set the grantor
        // offered (the picker submits the closed subtree), matching the explicit-only
        // invariant `OrgTree` applies to shared orgs.
        if (item.sharingType === 'ORG') orgIds.add(item.assetId)
        else if (item.sharingType === 'SITE') siteIds.add(item.assetId)
      }
    }

    return { orgIds, siteIds, isLoading: !skip && isLoading, isFetching: !skip && isFetching }
  }, [currentData, skip, isLoading, isFetching])
}

/**
 * DepotModels for sites the cloaked org can see, including shared ones.
 *
 * Synop's principal site cache is capped at two pages of 1000 — shared sites whose
 * owners sit deep in the customer tree are often absent, so the agreements allowlist
 * alone has nothing to admit. An arg-scoped `getDepots` (cache key includes
 * `organizationId`) fetches the cloaked org's own view without invalidating the
 * principal-scoped manager cache. Empty responses are ignored; they must not wipe
 * sites already present from the principal list.
 */
function useCloakedOrgSites(orgId: string | undefined, enabled: boolean) {
  const skip = !enabled || !orgId

  const {
    currentData: page0,
    isLoading: isPage0Loading,
    isFetching: isPage0Fetching
  } = useGetDepotsQuery(
    { organizationId: orgId ? [orgId] : undefined, count: ORG_SCOPED_DEPOTS_PAGE_SIZE, page: 0 },
    { skip }
  )
  const {
    currentData: page1,
    isLoading: isPage1Loading,
    isFetching: isPage1Fetching
  } = useGetDepotsQuery(
    { organizationId: orgId ? [orgId] : undefined, count: ORG_SCOPED_DEPOTS_PAGE_SIZE, page: 1 },
    { skip }
  )

  return useMemo(
    () => ({
      sites: skip ? ([] as DepotModel[]) : [...(page0?.items ?? []), ...(page1?.items ?? [])],
      isLoading: !skip && (isPage0Loading || isPage1Loading),
      isFetching: !skip && (isPage0Fetching || isPage1Fetching)
    }),
    [skip, page0, page1, isPage0Loading, isPage1Loading, isPage0Fetching, isPage1Fetching]
  )
}

function useFilteredSites(data: DepotModel[], filters?: SiteFilters) {
  const currentOrgId = useCurrentOrgId()
  const { orgTree } = useManagedOrgTree()

  const normalizedFilters = useNormalizedFilters(filters, {
    orgId: currentOrgId || undefined
  })
  const { orgId } = normalizedFilters

  const resolveShares = useShouldResolveCloakedShares(orgId)
  const shares = useReceivedShares(orgId, resolveShares)
  const cloakedOrgSites = useCloakedOrgSites(orgId, resolveShares)

  const sites = useMemo(() => {
    if (orgId == null) {
      return data
    }

    // Union principal cache with the cloaked org's own depot list so shared sites
    // missing from the capped principal pages are still present to filter.
    const byId = new Map<string, DepotModel>()
    for (const site of data) byId.set(site.depotId, site)
    for (const site of cloakedOrgSites.sites) byId.set(site.depotId, site)

    const descendants = orgTree.indexes.descendantIds.get(orgId)
    // Sites the cloaked org's own `getDepots` returned are authoritative for what
    // that org can see (owned + shared). Trust them even when the agreements
    // payload is empty or shaped differently than we expect.
    const cloakedDepotIds = new Set(cloakedOrgSites.sites.map((site) => site.depotId))

    return [...byId.values()].filter((site) => {
      const fleetId = site.fleetId ?? ''
      if (fleetId === orgId || descendants?.has(fleetId)) return true
      if (cloakedDepotIds.has(site.depotId)) return true
      return shares.orgIds.has(fleetId) || shares.siteIds.has(site.depotId)
    })
  }, [data, orgId, orgTree, shares, cloakedOrgSites.sites])

  return {
    sites,
    isSharesLoading: shares.isLoading || cloakedOrgSites.isLoading,
    isSharesFetching: shares.isFetching || cloakedOrgSites.isFetching
  }
}
